import dayjs, { Dayjs } from 'dayjs';
import isBetweenPlugin from 'dayjs/plugin/isBetween';
import isSameOrAfterPlugin from 'dayjs/plugin/isSameOrAfter';

import { formatDate as kitchenFormatDate } from '@thefarmersfront/kitchen-utensil';

import { DATE_FORMAT_DEFAULT, DATE_FORMAT_TO_SECOND } from '../constants';

dayjs.locale('ko');

dayjs.extend(isBetweenPlugin);
dayjs.extend(isSameOrAfterPlugin);

/** @description 날짜를 주어진 포맷대로 변경해주는 함수 */
export const formatDate = ({
  value = dayjs(),
  format = DATE_FORMAT_DEFAULT,
  separator = '-',
  fallback = '',
}: {
  value?: string | Dayjs;
  format?: string;
  separator?: string;
  fallback?: string;
} = {}): string => {
  if (typeof separator === 'string' && separator !== '-') {
    format = format.replaceAll('-', separator);
  }

  const result = kitchenFormatDate(value, { format, fallback });
  return result ?? fallback;
};

/** @description 두 개의 문자열을 받아 같은 날짜인지 비교 후 결과 반환 */
export const isSameDate = (date1: string, date2: string): boolean | null => {
  if (!dayjs(date1).isValid() || !dayjs(date2).isValid()) {
    return null;
  }
  return dayjs(date1).isSame(dayjs(date2));
};

/** @description 입력된 millisecond가 현재 시간과 얼마나 차이나는지 반환하는 함수 */
export const getDiffTime = (millisecond: number, unit: dayjs.QUnitType | dayjs.OpUnitType = 'minute'): number => {
  const currentTime = dayjs();
  const millisecondTime = dayjs(millisecond).format(DATE_FORMAT_TO_SECOND);

  return dayjs(millisecondTime).diff(currentTime, unit);
};

/**
 * @description 날짜가 입력된 날짜와 같거나 그 이후인지 확인 후 결과 반환
 * @example
 * isSameOrAfterDate('2025-08-18 10:00:00', '2025-08-18 09:00:00', 'second') // true
 */
export const isSameOrAfterDate = (date: string, targetDate: string, unit: dayjs.OpUnitType = 'day'): boolean => {
  return dayjs(date).isSameOrAfter(dayjs(targetDate), unit);
};

/** @description 날짜가 입력된 날짜보다 이후인지 확인 후 결과 반환 */
export const isAfterDate = (date: string, targetDate: string, unit: dayjs.OpUnitType = 'day'): boolean => {
  return dayjs(date).isAfter(dayjs(targetDate), unit);
};

/** @description 현재로부터 N일이 지났는지 여부를 반환하는 함수 */
export const checkOverNDays = (date: Dayjs | null, maxDay: number): boolean => {
  const today = dayjs();
  return today.diff(date, 'd') > maxDay;
};

/** @description 입력한 값이 유효한 날짜인지 반환하는 함수 */
export const isValidDay = (day: Dayjs | null): boolean => {
  return dayjs(day).isValid();
};

/** @description 날짜에 입력된 값을 더한 후 결과를 반환하는 함수 */
export const addDate = ({
  amount,
  date = dayjs(),
  unit = 'day',
}: {
  amount: number;
  date?: string | Dayjs | Date;
  unit?: dayjs.ManipulateType;
}): Dayjs => {
  return dayjs(date).add(amount, unit);
};
